import React, { useState, useEffect } from 'react';
import ImageUploader from './ImageUploader';
import HTMLEditor from './HTMLEditor';
import { FiPlus, FiTrash2 } from 'react-icons/fi';

const ArticleForm = ({ 
  initialData = {}, 
  onSubmit, 
  isLoading = false 
}) => {
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    description: '',
    content: '',
    imageUrl: '',
    categoryId: '',
    isPublished: false,
    keywords: '',
    author: 'smart-portfoy', // Varsayılan yazar adı
    sections: [],
    ...initialData
  });

  useEffect(() => {
    // initialData değiştiğinde formu güncelle
    if (Object.keys(initialData).length > 0) {
      setFormData({
        title: '',
        slug: '',
        description: '',
        content: '',
        imageUrl: '',
        categoryId: '',
        isPublished: false,
        keywords: '',
        author: 'smart-portfoy', // Varsayılan yazar adı
        sections: [],
        ...initialData
      });
    }
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleContentChange = (content) => {
    setFormData(prev => ({
      ...prev,
      content
    }));
  };

  const handleImageUpload = (url) => {
    setFormData(prev => ({ ...prev, imageUrl: url }));
  };

  // Slug oluşturma fonksiyonu
  const generateSlug = (text) => {
    return text
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-şçöğüıİŞÇÖĞÜ]/g, '')
      .replace(/ş/g, 's')
      .replace(/ç/g, 'c')
      .replace(/ğ/g, 'g')
      .replace(/ü/g, 'u')
      .replace(/ö/g, 'o')
      .replace(/ı/g, 'i')
      .replace(/İ/g, 'i')
      .replace(/Ş/g, 's')
      .replace(/Ç/g, 'c')
      .replace(/Ğ/g, 'g')
      .replace(/Ü/g, 'u')
      .replace(/Ö/g, 'o')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');
  };

  // Başlık değiştiğinde otomatik slug oluştur
  useEffect(() => {
    // Eğer slug manuel olarak değiştirildiyse, otomatik güncellemeyi durdur
    if (formData.slug !== initialData.slug && !formData.slugManuallyEdited) {
      setFormData(prev => ({
        ...prev,
        slug: generateSlug(formData.title)
      }));
    }
  }, [formData.title, initialData.slug, formData.slugManuallyEdited, formData.slug]);

  const handleSlugChange = (e) => {
    setFormData(prev => ({
      ...prev,
      slug: e.target.value,
      slugManuallyEdited: true
    }));
  };

  // Bölüm ekleme ve silme
  const addSection = () => {
    setFormData(prev => ({
      ...prev,
      sections: [...prev.sections, { title: '', description: '', points: [] }]
    }));
  };

  const removeSection = (index) => {
    setFormData(prev => ({
      ...prev,
      sections: prev.sections.filter((_, i) => i !== index)
    }));
  };

  const updateSection = (index, field, value) => {
    const updatedSections = [...formData.sections];
    updatedSections[index] = { 
      ...updatedSections[index],
      [field]: value 
    };
    setFormData(prev => ({ ...prev, sections: updatedSections }));
  };

  const addPoint = (sectionIndex) => {
    const updatedSections = [...formData.sections];
    updatedSections[sectionIndex].points = [
      ...(updatedSections[sectionIndex].points || []),
      ''
    ];
    setFormData(prev => ({ ...prev, sections: updatedSections }));
  };

  const updatePoint = (sectionIndex, pointIndex, value) => {
    const updatedSections = [...formData.sections];
    updatedSections[sectionIndex].points[pointIndex] = value;
    setFormData(prev => ({ ...prev, sections: updatedSections }));
  };

  const removePoint = (sectionIndex, pointIndex) => {
    const updatedSections = [...formData.sections];
    updatedSections[sectionIndex].points = updatedSections[sectionIndex].points.filter((_, i) => i !== pointIndex);
    setFormData(prev => ({ ...prev, sections: updatedSections }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Başlık */}
      <div>
        <label htmlFor="title" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Başlık
        </label>
        <input
          type="text"
          id="title"
          name="title"
          value={formData.title}
          onChange={handleChange}
          required
          className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:bg-gray-700 dark:text-white"
        />
      </div>

      {/* Yazar */}
      <div>
        <label htmlFor="author" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Yazar
        </label>
        <input
          type="text"
          id="author"
          name="author"
          value={formData.author}
          onChange={handleChange}
          className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:bg-gray-700 dark:text-white"
        />
      </div>

      {/* Slug */}
      <div>
        <label htmlFor="slug" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Slug URL
        </label>
        <input
          type="text"
          id="slug"
          name="slug"
          value={formData.slug}
          onChange={handleSlugChange}
          required
          className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:bg-gray-700 dark:text-white"
        />
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Örnek: {window.location.origin}/egitim/{formData.slug}
        </p>
      </div>

      {/* Açıklama */}
      <div>
        <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Açıklama
        </label>
        <textarea
          id="description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          rows={3}
          className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:bg-gray-700 dark:text-white"
        />
      </div>

      {/* Resim Yükleyici */}
      <ImageUploader
        initialImage={formData.imageUrl}
        onImageUpload={handleImageUpload}
      />

      {/* İçerik - HTML Editor ile */}
      <HTMLEditor 
        value={formData.content}
        onChange={handleContentChange}
        rows={15}
      />

      {/* Anahtar Kelimeler */}
      <div>
        <label htmlFor="keywords" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Anahtar Kelimeler (virgülle ayırın)
        </label>
        <input
          type="text"
          id="keywords"
          name="keywords"
          value={formData.keywords}
          onChange={handleChange}
          className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:bg-gray-700 dark:text-white"
        />
      </div>

      {/* Yayın Durumu */}
      <div className="flex items-center">
        <input
          type="checkbox"
          id="isPublished"
          name="isPublished"
          checked={formData.isPublished}
          onChange={handleChange}
          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700"
        />
        <label htmlFor="isPublished" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
          Yayınla
        </label>
      </div>

      {/* Bölümler */}
      <div>
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300">Bölümler</h3>
          <button
            type="button"
            onClick={addSection}
            className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <FiPlus className="mr-1" /> Bölüm Ekle
          </button>
        </div>

        <div className="mt-4 space-y-6">
          {formData.sections.map((section, sectionIndex) => (
            <div key={sectionIndex} className="p-4 border rounded-md dark:border-gray-600">
              <div className="flex justify-between items-center mb-3">
                <h4 className="font-medium text-gray-700 dark:text-gray-300">Bölüm {sectionIndex + 1}</h4>
                <button
                  type="button"
                  onClick={() => removeSection(sectionIndex)}
                  className="text-red-500 hover:text-red-700"
                >
                  <FiTrash2 size={18} />
                </button>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Bölüm Başlığı
                  </label>
                  <input
                    type="text"
                    value={section.title}
                    onChange={(e) => updateSection(sectionIndex, 'title', e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:bg-gray-700 dark:text-white"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Bölüm Açıklaması
                  </label>
                  <textarea
                    value={section.description}
                    onChange={(e) => updateSection(sectionIndex, 'description', e.target.value)}
                    rows={2}
                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <div className="flex justify-between items-center">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Madde İşaretli Noktalar
                    </label>
                    <button
                      type="button"
                      onClick={() => addPoint(sectionIndex)}
                      className="inline-flex items-center px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none"
                    >
                      <FiPlus className="mr-1" /> Nokta Ekle
                    </button>
                  </div>

                  <div className="mt-2 space-y-2">
                    {section.points && section.points.map((point, pointIndex) => (
                      <div key={pointIndex} className="flex items-center">
                        <input
                          type="text"
                          value={point}
                          onChange={(e) => updatePoint(sectionIndex, pointIndex, e.target.value)}
                          className="flex-1 rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:bg-gray-700 dark:text-white"
                        />
                        <button
                          type="button"
                          onClick={() => removePoint(sectionIndex, pointIndex)}
                          className="ml-2 text-red-500 hover:text-red-700"
                        >
                          <FiTrash2 size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Submit butonu */}
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={isLoading}
          className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {isLoading ? 'Kaydediliyor...' : 'Kaydet'}
        </button>
      </div>
    </form>
  );
};

export default ArticleForm;